/** @typedef {import('../index.js').BaseDocData} BaseDocData */
/* eslint-disable no-underscore-dangle */
import * as Yup from 'yup';
import { LotInspectionResults } from '../../constants/index.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

/**
 * A QC inspection record in the top-level `lotInspections` collection. One
 * record covers one or more lots (`lotIds`) — at launch checks are done
 * per bag, but sampling can later attach a single record to many bags
 * without a data migration. A lot's inspection history is queried with
 * `lotIds array-contains <lotId>` (no pointer on the lot — re-inspections
 * just add records); the lot's status reflects the outcome.
 */
export const lotInspectionSchema = Yup.object().shape({
  lotIds: Yup.array().of(Yup.string()).min(1, 'At least one lot is required').required('Lot IDs are required'),
  result: Yup.string().oneOf(Object.values(LotInspectionResults)).required('Result is required'),
  // trim runs as a transform first, so a whitespace-only note fails required.
  note: Yup.string().trim().required('Note is required'),
  /**
   * URLs to measurement data, photos, reports. Required (per Harry) —
   * every inspection must point at its evidence. http(s) only, matching
   * the modal — these render as clickable links in the UI.
   */
  dataLinks: Yup.array().of(
    Yup.string()
      .trim()
      .required('Data links cannot be blank')
      .url('Data links must be valid URLs')
      .matches(/^https?:\/\//, 'Data links must be http(s) URLs'),
  ).min(1, 'At least one data link is required')
    .required('Data links are required'),
  /**
   * Required, like the lot's: inspections are always recorded by a
   * signed-in inspector, and the history sorts by _createdBy.timestamp.
   */
  _createdBy: Yup.object().shape({
    uid: Yup.string().required('_createdBy.uid is required'),
    email: Yup.string().email().required('_createdBy.email is required'),
    timestamp: YupFirestoreTimestamp.nullable(),
  }).required('_createdBy is required'),
});

/**
 * @typedef {Yup.InferType<typeof lotInspectionSchema>} LotInspectionSchema
 * @typedef {BaseDocData & LotInspectionSchema} LotInspectionDocData
 */

export default class LotInspection {
  constructor({
    id, lotIds, result, note, dataLinks, _createdBy,
  }) {
    const validatedData = lotInspectionSchema.validateSync({
      lotIds,
      result,
      note,
      dataLinks,
      _createdBy,
    });

    this.id = id || null;
    this.lotIds = validatedData.lotIds;
    this.result = validatedData.result;
    this.note = validatedData.note;
    this.dataLinks = validatedData.dataLinks || [];
    this._createdBy = validatedData._createdBy;
  }

  toFirestore({ serverTimestamp }) {
    return {
      lotIds: [...this.lotIds],
      result: this.result,
      note: this.note,
      dataLinks: [...this.dataLinks],
      // Rebuilt as a plain object — Firestore rejects Yup's cast objects.
      _createdBy: {
        uid: this._createdBy.uid,
        email: this._createdBy.email,
        timestamp: this._createdBy.timestamp || serverTimestamp(),
      },
    };
  }

  static fromFirestore(docData) {
    return new LotInspection(docData);
  }
}
